import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { getClient } from '../lib/supabase';
import { useAuth } from '../lib/auth';

/**
 * OAuth consent — the one place an AI tool is let through the AI door (security worklist B7.3).
 *
 * Supabase Auth's OAuth 2.1 server sends an AI client here with ?authorization_id=… . This page shows which client is
 * asking and for what, and the founder approves or denies. On approval Supabase issues the client a token; the door
 * server (supabase/functions/ai-door) then serves it only the fixed, non-personal tools over the ai_door login.
 *
 * It renders inside AdminLayout, so sign-in and the authenticator-code gate (MfaGate) have already run. Only a
 * superuser may approve. A visitor who arrives signed out is sent to login and brought back here (App.tsx keeps the
 * link in localStorage under OAUTH_RESUME_KEY, for 10 minutes).
 */
export const OAUTH_RESUME_KEY = 'votd.oauth.resume';
const RESUME_LIFE_MS = 10 * 60 * 1000; // Supabase's authorization request lives 10 minutes

/** Keep a pending approval link across sign-in. localStorage, not sessionStorage: the sign-in email link opens a new tab. */
export function rememberOAuthResume(pathAndSearch: string): void {
  try { localStorage.setItem(OAUTH_RESUME_KEY, JSON.stringify({ to: pathAndSearch, at: Date.now() })); } catch { /* unavailable */ }
}

/** The pending approval link, if one is fresh; null otherwise. */
export function pendingOAuthResume(): string | null {
  try {
    const v = JSON.parse(localStorage.getItem(OAUTH_RESUME_KEY) ?? 'null') as { to?: string; at?: number } | null;
    if (v?.to && v.to.startsWith('/oauth/consent?') && typeof v.at === 'number' && Date.now() - v.at < RESUME_LIFE_MS) return v.to;
  } catch { /* unavailable or malformed */ }
  return null;
}

export function clearOAuthResume(): void {
  try { localStorage.removeItem(OAUTH_RESUME_KEY); } catch { /* unavailable */ }
}

type Details = {
  authorization_id?: string;
  redirect_uri?: string;
  redirect_url?: string;
  scope?: string;
  client?: { client_id?: string; client_name?: string; client_uri?: string; logo_uri?: string };
};

export function OAuthConsent() {
  const [params] = useSearchParams();
  const authorizationId = params.get('authorization_id');
  const { user } = useAuth();
  const client = getClient('stage');
  const [details, setDetails] = useState<Details | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    clearOAuthResume();
    if (!authorizationId || !client) return;
    let cancelled = false;
    (async () => {
      const { data, error: e } = await client.auth.oauth.getAuthorizationDetails(authorizationId);
      if (cancelled) return;
      if (e) { setError(e.message); return; }
      const d = data as Details;
      // Consent already given: Supabase answers with only a redirect.
      if (d?.redirect_url && !d.client) { window.location.assign(d.redirect_url); return; }
      setDetails(d);
    })();
    return () => { cancelled = true; };
  }, [authorizationId, client]);

  async function decide(approve: boolean) {
    if (!authorizationId || !client) return;
    setBusy(true);
    const { data, error: e } = approve
      ? await client.auth.oauth.approveAuthorization(authorizationId)
      : await client.auth.oauth.denyAuthorization(authorizationId);
    if (e) { setError(e.message); setBusy(false); return; }
    const url = (data as { redirect_url?: string } | null)?.redirect_url;
    if (url) window.location.assign(url); else setBusy(false);
  }

  const superuser = user?.role === 'superuser';

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-2xl">
      <div>
        <h1 className="text-xl font-semibold text-stone-900 flex items-center gap-2">
          <ShieldCheck size={20} className="text-stone-500" /> Approve an AI tool
        </h1>
        <p className="text-sm text-stone-500 mt-0.5">
          An approved tool can use the AI door only: the votes calendar, the backlog and system health. It cannot reach
          member data, and it cannot run its own queries.
        </p>
      </div>

      {!authorizationId && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900">
          This link has no authorization request in it. Start the connection again from the AI tool.
        </div>
      )}
      {!superuser && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-900">
          Only a superuser can approve AI tools.
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-900">{error}</div>
      )}

      {details && superuser && (
        <div className="bg-white border border-stone-200 rounded-xl p-4 space-y-3">
          <dl className="text-sm grid grid-cols-[8rem_1fr] gap-y-2">
            <dt className="text-stone-500">Tool</dt>
            <dd className="text-stone-900 font-medium">{details.client?.client_name ?? details.client?.client_id ?? 'Unknown client'}</dd>
            <dt className="text-stone-500">Client id</dt>
            <dd className="text-stone-700 font-mono text-xs break-all">{details.client?.client_id ?? '—'}</dd>
            <dt className="text-stone-500">Returns to</dt>
            <dd className="text-stone-700 font-mono text-xs break-all">{details.redirect_uri ?? '—'}</dd>
            <dt className="text-stone-500">Asks for</dt>
            <dd className="text-stone-700">{details.scope || 'sign-in only'}</dd>
          </dl>
          <p className="text-xs text-stone-500">
            Approve only if you started this connection yourself just now. You can withdraw it later from this account's
            connected apps.
          </p>
          <div className="flex gap-2">
            <button
              type="button" disabled={busy} onClick={() => decide(true)}
              className="px-4 py-2 rounded-lg bg-stone-900 text-white text-sm disabled:opacity-50"
            >Approve</button>
            <button
              type="button" disabled={busy} onClick={() => decide(false)}
              className="px-4 py-2 rounded-lg border border-stone-300 text-stone-700 text-sm disabled:opacity-50"
            >Deny</button>
          </div>
        </div>
      )}
    </div>
  );
}
